import classNames from 'classnames';
import { first, isArray, isBoolean, isString } from 'lodash';
import { useEffect, useMemo } from 'react';

import { isValid } from 'date-fns';

import { notNil } from '@/lib/utils';
import { IconButton } from '@/components/button';
import { Dropdown, DropdownMenuItem, DropdownIconSizePx } from '@/components/dropdown';
import { DateTimeInput } from '@/components/form/datetime-input';
import { Input } from '@/components/form/input';
import { PenguinoInput, PenguinoVersion } from '@/components/form/penguino-input';
import { SearchInput } from '@/components/form/search-input';
import { Select } from '@/components/form/select';
import { Icon } from '@/components/icon';
import { EnumOptionsQuery, useEnumOptionsQuery } from '@/graphql';
import { useAccountContext, useAccountTimezone } from '@/lib/accounts/context';
import { useTrackEvent } from '@/lib/analytics';
import { DateRange, getDefaultFormattedLocalDate, isDateRange } from '@/lib/date';
import { TagInput } from '@/components/form/tag-input';
import { VariableReferenceBadge } from '@/explore/components/variable-reference-badge';
import { variableColors } from '@/explore/components/variable-color';
import { useExplorationContext } from '@/explore/exploration/exploration-context';
import { isValueExpression } from '@/explore/pipeline/operation';
import { FieldGroup, FieldOrGroup, findField, firstField, isField } from '@/explore/pipeline/utils';
import {
  getQueryVariableTypeFromVariable,
  getVariableColor,
  getVariableExpression,
  isNumberType,
} from '@/explore/utils';
import { createFieldOptionGrouped } from '@/explore/components/select-options/field';
import { getFilterVariableValueType } from '@/core/operation';

import type {
  EnumOptions,
  Field,
  FilterCondition,
  FilterOperator,
  FilterValue,
  Model,
  VariableDefinition,
} from '../../types';
import {
  ensureStableValue,
  filterOperators,
  filterUnaryOperators,
  isBinaryFilterOperator,
  parseFilterValue,
  requiresArrayValue,
  stringifyFilterValue,
  UIFilterOperator,
  getOperatorLabel,
  FilterBinaryOperator,
  formatNumericFilterValue,
} from '../utils/filter';

import form from '@/components/form/form.module.scss';
import styles from '../pipeline.module.scss';

/**
 * Since the UI supports some operators that the backend doesn't, we need to do
 * a bit of back-and-forth conversion between the two filter shapes.
 * See FilterOperatorExtended vs FilterOperator under types.
 */
const convertFilterToOperationParameters = (
  field: Field,
  operator: UIFilterOperator,
  value: FilterValue | undefined,
): FilterCondition => {
  if (operator === 'istrue' || operator === 'isfalse') {
    value = operator === 'istrue' ? true : false;
    operator = '==';
  }
  return isBinaryFilterOperator(operator)
    ? { key: field.key, operator, value }
    : { key: field.key, operator };
};

const convertOperationParametersToFilter = (
  parameters: FilterCondition,
  fields: FieldOrGroup[],
): {
  field: Field;
  operator: UIFilterOperator;
  value: FilterValue;
} => {
  const lookupKey = [parameters.relation?.modelId, parameters.relation?.key, parameters.key]
    .filter(notNil)
    .join('.');

  const field = findField(fields, lookupKey) ?? firstField(fields);
  if (field === undefined) {
    throw new Error(`No fields to filter by`);
  }

  let operator = parameters.operator as UIFilterOperator;
  let value = parameters.value ?? '';

  if (field.type === 'Boolean' && operator === '==' && isBoolean(value)) {
    operator = parameters.value === true ? 'istrue' : 'isfalse';
    value = '';
  }

  return { field, operator, value };
};

const isEmptyOperationValueAllowed = (operator: UIFilterOperator | FilterOperator) =>
  !isBinaryFilterOperator(operator) || operator === '==' || operator === '!=';

const getOperatorsForField = (field: Field): readonly UIFilterOperator[] => {
  switch (field.type) {
    case 'Date':
      return filterOperators.filter(
        (operator) =>
          ![
            'arrcontains',
            'noticontains',
            'icontains',
            'istrue',
            'isfalse',
            'in',
            'notin',
          ].includes(operator),
      );
    case 'Number':
    case 'Integer':
    case 'Float':
      return filterOperators.filter(
        (operator) =>
          !['arrcontains', 'noticontains', 'icontains', 'istrue', 'isfalse'].includes(operator),
      );
    case 'Boolean':
      return filterUnaryOperators;
    case 'Object':
      return ['==', '!=', 'isnull', 'isnotnull', 'icontains', 'noticontains'];
    case 'Array':
      return ['arrcontains', ...filterUnaryOperators];
    case 'String':
      return filterOperators.filter(
        (operator) =>
          !['arrcontains', 'istrue', 'isfalse', '>', '>=', '<', '<='].includes(operator),
      );
    case 'Enum':
    case 'Interval':
    case null:
      return filterOperators.filter(
        (operator) => !['arrcontains', 'istrue', 'isfalse'].includes(operator),
      );
  }
};

interface FieldInputProps {
  fields: FieldOrGroup[];
  fieldKey: string;
  setFieldKey: (field: string) => void;
}

const FieldInput = (props: FieldInputProps) => {
  const { fields, fieldKey, setFieldKey } = props;
  return (
    <SearchInput
      options={fields.map(createFieldOptionGrouped)}
      value={fieldKey}
      onChange={setFieldKey}
      autoFocus
    />
  );
};

interface OperatorInputProps {
  field: Field;
  operator: UIFilterOperator;
  setOperator: (operator: string) => void;
}

const OperatorInput = (props: OperatorInputProps) => {
  const { field, operator, setOperator } = props;
  const options = getOperatorsForField(field).map((operator) => ({
    value: operator,
    label: getOperatorLabel(operator, field.type),
  }));
  return <Select options={options} value={operator} onChange={setOperator} />;
};

interface ValueInputProps {
  field: Field;
  fields: FieldOrGroup[];
  enumOptions: EnumOptions;
  loading: boolean;
  operator: UIFilterOperator;
  value: FilterValue;
  setValue: (value: FilterValue | undefined) => void;
  model?: Model;
  timezone: string;
}

const ValueInput = (props: ValueInputProps) => {
  const { field, enumOptions, operator, value, timezone, setValue } = props;
  const { getVariables } = useExplorationContext();

  if (!isBinaryFilterOperator(operator)) {
    return null;
  }

  if (isValueExpression(value)) {
    return (
      <div className={form.formRowInner}>
        <PenguinoInput
          fields={props.fields}
          model={props.model}
          variables={getVariables()}
          placeholder="Enter custom formula..."
          value={value.expression}
          onChange={(expression) => setValue({ expression, version: PenguinoVersion })}
          requiredType={field.type ?? undefined}
          // Keep field order as-is
          autocompleteSort={() => 1}
        />
      </div>
    );
  }

  if (field.type === 'Date') {
    const defaultValue = getDefaultFormattedLocalDate();
    const mode = ['==', '!='].includes(operator) ? 'range' : 'single';

    const val =
      value === undefined
        ? { from: defaultValue, to: defaultValue }
        : isDateRange(value)
          ? value
          : isString(value) && isValid(new Date(value))
            ? { from: value, to: value }
            : { from: defaultValue, to: defaultValue };

    const handleChange = (range: Partial<DateRange>) => {
      if (isDateRange(range) && mode === 'range') {
        return setValue(range);
      }

      if (mode === 'single') {
        return setValue(range.from ?? defaultValue);
      }

      throw new Error('Invalid date range');
    };

    return (
      <DateTimeInput
        mode={mode}
        isRequired
        value={val}
        timezone={timezone}
        onChange={handleChange}
      />
    );
  }

  // The condition here must match with the "skip" parameter in the enumOptions query
  if (
    field.type === 'Enum' &&
    !['icontains', 'noticontains'].includes(operator) &&
    field.model !== undefined
  ) {
    const convertValue = (v: unknown): string => String(v);
    const validValue =
      value === undefined
        ? undefined
        : requiresArrayValue(operator)
          ? isArray(value)
            ? value.map(convertValue)
            : [convertValue(value)]
          : convertValue(value);
    return (
      <SearchInput
        options={enumOptions ?? []}
        value={validValue}
        onChange={setValue}
        loading={props.loading}
        closeOnSelect={!isArray(value)}
        allowCustomValue
      />
    );
  }

  if (Array.isArray(value)) {
    return (
      <TagInput
        required={!isEmptyOperationValueAllowed(operator)}
        value={value.map((v) => stringifyFilterValue(v)).filter((v) => v !== '')}
        placeholder={'Value1, Value2, ...'}
        onChange={(value) =>
          setValue(
            isNumberType(field.type) &&
              value.every((v) => v !== '' && !isNaN(Number(v)) && String(v).at(-1) !== '.')
              ? value.map(Number)
              : value,
          )
        }
      />
    );
  }

  const isNumberField = isNumberType(field.type);

  const formattedValue = isNumberField
    ? // Value needs to remain string for native inputs to reactively update displayed value (if input type changes)
      formatNumericFilterValue(value).toString()
    : stringifyFilterValue(value);

  return (
    <Input
      required={!isEmptyOperationValueAllowed(operator)}
      type={isNumberField ? 'number' : 'text'}
      step="any"
      value={formattedValue}
      placeholder={isNumberField ? 'Number' : 'Value'}
      onChange={(e) => setValue(parseFilterValue(e.target.value, field.type, operator))}
    />
  );
};

interface FilterConditionFormProps {
  fields: FieldOrGroup[];
  fieldsForExpression: (Field | FieldGroup)[];
  condition: FilterCondition;
  setCondition: (condition: FilterCondition) => void;
  variables: VariableDefinition[];
  model?: Model;
  onRemove?: () => void;
}

const validateVariable = (
  variable: VariableDefinition,
  filterOperator: FilterBinaryOperator,
  fieldType: Field['type'],
): { error: string | null } => {
  if (variable.kind === 'multiselect' && !['in', 'notin'].includes(filterOperator)) {
    return {
      error: "Multi-select variables can only be used with 'is one of' and 'is not one of' filters",
    };
  }
  if (variable.kind !== 'multiselect' && ['in', 'notin'].includes(filterOperator)) {
    return {
      error: "'is one of' and 'is not one of' filters can only be used with multi-select variables",
    };
  }

  const expectedType = getFilterVariableValueType(filterOperator, fieldType);
  const variableType = getQueryVariableTypeFromVariable(variable);
  if (variableType !== null && variableType !== expectedType) {
    return {
      error: `Variable type is incorrect. Expected type ${expectedType} but got ${variableType}.`,
    };
  }

  return { error: null };
};

export const FilterConditionForm = (props: FilterConditionFormProps) => {
  const { model, fieldsForExpression, condition, setCondition, variables, onRemove } = props;
  const { account } = useAccountContext();
  const timezone = useAccountTimezone();
  const { exploration } = useExplorationContext();
  const trackEvent = useTrackEvent();

  const fields = props.fields.map((item) =>
    isField(item)
      ? item
      : { ...item, fields: item.fields.map((f) => ({ ...f, key: `${item.key}.${f.key}` })) },
  );

  const { field, operator, value } = convertOperationParametersToFilter(condition, fields);
  if (field === undefined) {
    throw new Error(`Field not found for key ${condition.key}`);
  }

  const valueIsExpression = isValueExpression(value);

  const valueIsVariableReference =
    valueIsExpression &&
    variables.some((variable) => getVariableExpression(variable.key) === value.expression);

  const shouldFetchEnumOptions =
    !valueIsExpression &&
    field.type === 'Enum' &&
    field.model !== undefined &&
    !['icontains', 'noticontains'].includes(operator);

  const { data, loading, refetch } = useEnumOptionsQuery({
    variables: {
      accountId: account.accountId,
      modelId: field.model?.modelId ?? '',
      propertyKey: field.model?.propertyKey ?? '',
    },
    skip: !shouldFetchEnumOptions,
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      const options = extractOptions(data);
      if (value === undefined || (value === '' && options.length > 0)) {
        const nextValue = requiresArrayValue(operator)
          ? [String(options[0].value)]
          : String(options[0].value);
        setCondition({ ...condition, value: nextValue });
      }
    },
  });

  const enumOptions = extractOptions(data);

  const valueMatchesOptions = useMemo(() => {
    if (enumOptions.length === 0 || value === undefined || value === '') {
      return true;
    }

    if (requiresArrayValue(operator) && Array.isArray(value)) {
      const valueStrings = value.map((v) => String(v));
      return valueStrings.every((v) => enumOptions.some((option) => option.value === v));
    }
    return enumOptions.some((option) => option.value === value);
  }, [enumOptions, value, operator]);

  // Refetch enum options if current value doesn't match any cached options
  useEffect(() => {
    if (shouldFetchEnumOptions && !loading && enumOptions.length > 0 && !valueMatchesOptions) {
      refetch();
    }
    // Run only once to avoid fetch loop
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variableMenuItems: DropdownMenuItem[] =
    variables.length > 0
      ? [
          {
            type: 'divider',
          },
          ...variables.map((variable) => {
            const { error: disableTooltip } = isBinaryFilterOperator(condition.operator)
              ? validateVariable(variable, condition.operator, field.type)
              : { error: null };
            return {
              label: variable.key,
              icon: <Icon name="VariableInstance" size={DropdownIconSizePx} />,
              onClick: () => {
                trackEvent('Variable Assigned', {
                  exploration,
                  variable: value,
                });

                return handleChange(field, operator, {
                  expression: getVariableExpression(variable.key),
                  version: PenguinoVersion,
                });
              },
              ...(disableTooltip !== null
                ? {
                    disabled: true,
                    tooltip: disableTooltip,
                  }
                : {
                    color: getVariableColor(variable.key, variables),
                  }),
            };
          }),
        ]
      : [];

  const menuItems: DropdownMenuItem[] = [
    {
      icon: <Icon name="CustomFormula" size={DropdownIconSizePx} />,
      label: 'Custom Formula',
      className: classNames({ [styles.customFormulaActive]: valueIsExpression }),
      onClick: () =>
        handleChange(
          field,
          operator,
          valueIsExpression ? '' : { expression: '', version: PenguinoVersion },
        ),
      disabled: !isBinaryFilterOperator(operator),
    },
    ...variableMenuItems,
  ];

  if (onRemove !== undefined) {
    menuItems.push({
      label: 'Delete',
      onClick: onRemove,
      icon: <Icon name="Trash2" size={DropdownIconSizePx} />,
    });
  }

  const handleChange = (
    field: Field,
    operator: UIFilterOperator,
    value: FilterValue | undefined,
  ) => {
    const condition = convertFilterToOperationParameters(field, operator, value);

    if (condition.key.includes('.')) {
      const [modelId, relationKey, fieldKey] = condition.key.split('.');

      setCondition({
        ...condition,
        key: fieldKey,
        relation: { key: relationKey, modelId },
      });

      return;
    }

    setCondition(condition);
  };

  if (field.type === 'Boolean') {
    return (
      <BooleanFilterConditionForm
        operator={operator}
        value={value}
        field={field}
        fields={fields}
        variables={variables}
        onChange={handleChange}
        onRemove={onRemove}
      />
    );
  }

  return (
    <>
      <div className={form.formRow}>
        <FieldInput
          fields={fields}
          fieldKey={field.key}
          setFieldKey={(key) => {
            const newField = findField(fields, key);
            if (newField === undefined) {
              throw new Error(`Field not found: ${key}`);
            }

            const newOperators = getOperatorsForField(newField);
            const newOperator = newOperators.includes(operator)
              ? operator
              : (newOperators.at(0) ?? '==');
            handleChange(newField, newOperator, ensureStableValue(newField, newOperator, value));
          }}
        />
        <div className={form.narrow}>
          <OperatorInput
            field={field}
            operator={operator}
            setOperator={(v) => {
              handleChange(
                field,
                v as UIFilterOperator,
                ensureStableValue(field, v as UIFilterOperator, value),
              );
            }}
          />
        </div>
      </div>
      <div className={classNames(form.formRow, form.alignTop)}>
        {valueIsVariableReference ? (
          <VariableReferenceBadge
            value={value.expression}
            variables={variables}
            onRemove={() => {
              trackEvent('Variable Unassigned', { exploration, variable: value });

              return handleChange(field, operator, field.type === 'Boolean' ? true : '');
            }}
          />
        ) : (
          <ValueInput
            fields={fieldsForExpression}
            field={field}
            model={model}
            enumOptions={enumOptions}
            loading={loading}
            operator={operator}
            value={value}
            setValue={(value) => handleChange(field, operator, value)}
            timezone={timezone}
          />
        )}

        {
          <Dropdown
            align="right"
            trigger={(isOpen, setIsOpen) => (
              <IconButton
                icon="MoreHorizontal"
                size="small"
                title="More..."
                type="gray"
                onClick={() => setIsOpen(!isOpen)}
              />
            )}
            items={menuItems}
          />
        }
      </div>
    </>
  );
};

const extractOptions = (data?: EnumOptionsQuery) =>
  (data?.account?.model?.property?.enumOptions ?? []).map(({ value, label }) => ({ value, label }));

interface BooleanFilterConditionFormProps {
  operator: UIFilterOperator;
  value: FilterValue;
  field: Field;
  fields: FieldOrGroup[];
  onChange: (field: Field, operator: UIFilterOperator, value: FilterValue) => void;
  onRemove?: () => void;
  variables: VariableDefinition[];
}

const BooleanFilterConditionForm = (props: BooleanFilterConditionFormProps) => {
  const { operator, value, field, fields, onChange, onRemove, variables } = props;
  const trackEvent = useTrackEvent();
  const { exploration } = useExplorationContext();
  const menuItems: DropdownMenuItem[] = [
    ...(onRemove !== undefined
      ? [
          {
            label: 'Delete',
            onClick: onRemove,
            icon: <Icon name="Trash2" size={DropdownIconSizePx} />,
          },
        ]
      : []),
    ...variables.map((variable) => ({
      label: variable.key,
      icon: <Icon name="VariableInstance" size={DropdownIconSizePx} />,
      color: getVariableColor(variable.key, variables, variableColors),
      onClick: () => {
        trackEvent('Variable Assigned', {
          explorationId: exploration.explorationId,
          variable: value,
        });

        return onChange(field, '==', {
          expression: getVariableExpression(variable.key),
          version: PenguinoVersion,
        });
      },
    })),
  ];

  return (
    <div className={form.formRow}>
      <FieldInput
        fields={fields}
        fieldKey={field.key}
        setFieldKey={(key) => {
          const newField = findField(fields, key);
          if (newField === undefined) {
            throw new Error(`Field not found: ${key}`);
          }
          let newOperator = operator;
          let newValue = value;
          const newOperators = getOperatorsForField(newField);
          if (!newOperators.includes(operator)) {
            newValue = '';
            newOperator = first(newOperators) ?? '==';
          }
          onChange(newField, newOperator, newValue);
        }}
      />
      {isValueExpression(value) ? (
        <VariableReferenceBadge
          value={value.expression}
          variables={variables}
          onRemove={() => {
            trackEvent('Variable Unassigned', {
              explorationId: exploration.explorationId,
              variable: value,
            });
            return onChange(field, operator, true);
          }}
        />
      ) : (
        <div className={form.narrow}>
          <OperatorInput
            field={field}
            operator={operator}
            setOperator={(operator) => onChange(field, operator as UIFilterOperator, value)}
          />
        </div>
      )}

      {menuItems.length > 0 && (
        <Dropdown
          align="right"
          trigger={(isOpen, setIsOpen) => (
            <IconButton
              icon="MoreHorizontal"
              size="small"
              title="More..."
              type="gray"
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
          items={menuItems}
        />
      )}
    </div>
  );
};
